import React from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setDrawer } from "../../../redux-store/features/layoutSlice";
import { useAddSponsorUser } from "../../../custom-hooks/queries";
import NotificationModal from "../../../components/NotificationModal";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import { v4 as uuidv4 } from "uuid";
import {
  RIGHTS_DISPLAY_NAMES,
  ROLE_DISPLAY_NAMES,
  PERMISSION_CATEGORY_DISPLAY_NAMES,
} from "../../../utils/constants";
import { supportedEmailCharacters } from "../../../utils/emailSupportedCharacters";

const schema = yup.object().shape({
  email: yup.string().email("Invalid email").test(supportedEmailCharacters).required("Email is required"),
  first_name: yup.string().required("First name is required"),
  last_name: yup.string().required("Last name is required"),
  office_phone_number: yup
    .string()
    .matches(/^\+?[1-9]\d{1,14}$/, "Please enter a valid office phone number")
    .required("Office phone number is required")
    .transform((value) => (value === "" ? null : value)),
  phone_number: yup.string(),
  contact_preference: yup.string().required("Contact preference is required"),
  role: yup.array().min(1, "At least one role must be selected"),
});

// Role definitions with descriptions and default rights
const roleDefinitions = {
  "Sponsor Admin": {
    description: "",
    defaultRights: [
      "Find Providers",
      "Manage Users",
      "View Requested RFIs",
      "Request Assessment",
      "Request RFIs",
      "Compare RFI",
      "View Released RFIs",
      "Shared Assessment Calendar",
      "View and Request Library VQAs",
      "Receive Platform Notifications",
      "Status Dashboard",
      "Vendor Discovery",
      "Document Library",
      "Compare Vendors",
      "Clinical Indications Dashboard",
      "Manage Team",
      "Manage Organization",
      "Request Audit Reports",
      "View Audit Reports",
      "Download Audit Reports",
    ],
  },
  "RFI Manager": {
    description: "",
    defaultRights: [
      "View Requested RFIs",
      "Request RFIs",
      "Compare RFI",
      "View Released RFIs",
      "RFI Template Builder",
      "Download RFIs",
      "Manage Requested & Released RFIs",
      "Request New RFIs",
      "Risk Categorization",
    ],
  },
  "VQA Manager": {
    description: "",
    defaultRights: [
      "Request Assessment",
      "Shared Assessment Calendar",
      "View and Request Library VQAs",
      "Request/Manage New Assessments",
      "View My Assessment",
      "View Scheduled Assessments",
      "Join Scheduled Assessments",
    ],
  },
};

// Update the rightsCategories object to include all rights from rightsList
const rightsCategories = {
  "General Permissions": {
    description: "",
    rights: [
      "Status Dashboard",
      "Vendor Discovery",
      "Document Library",
      "Trial Experience Dashboard",
    ],
  },
  "RFI Permissions": {
    description: "",
    rights: [
      "Diligent Respond Access",
      "Request RFIs",
      "View Requested RFIs",
      "Manage Requested & Released RFIs",
      "View Released RFIs",
      "RFI Template Builder",
      "Risk Categorization",
      "Download RFIs",
      "Vendors",
    ],
  },
  "Assessment Permissions": {
    description: "",
    rights: [
      "Request/Manage New Assessments",
      "View My Assessment",
      "Request Audit Reports",
      "View Audit Reports",
      "Download Audit Reports",
      "View Scheduled Assessments",
      "Join Scheduled Assessments",
    ],
  },
  // "Audit Permissions": {
  //   description:
  //     "Access and management of audit reports and related documentation",
  //   rights: [
  //     "Request Audit Reports",
  //     "View Audit Reports",
  //     "Download Audit Reports",
  //   ],
  // },
  "Admin Permissions": {
    description: "",
    rights: ["Manage Team", "Manage Organization"],
  },
};

// Complete rights list for reference
const rightsList = [
  "Status Dashboard",
  "Vendor Discovery",
  "Document Library",
  "Trial Experience Dashboard",
  "Diligent Respond Access",
  "Request RFIs",
  "View Requested RFIs",
  "Manage Requested & Released RFIs",
  "View Released RFIs",
  "RFI Template Builder",
  "Risk Categorization",
  "Download RFIs",
  "Request/Manage New Assessments",
  "View My Assessment",
  "Request Audit Reports",
  "View Audit Reports",
  "Download Audit Reports",
  "View Scheduled Assessments",
  "Join Scheduled Assessments",
  "Manage Team",
  "Find Providers",
  "Manage Organization",
  // "Manage Users",
  // "Request Assessment",
  // "Request New RFIs",
  // "Compare RFI",
  // "Shared Assessment Calendar",
  // "View and Request Library VQAs",
];

// Create a rights descriptions object to store all descriptions
const rightsDescriptions = {
  // General Permissions (already implemented)
  "Find Providers": "Search for service providers across the platform",
  "Manage Users": "Add, edit, and remove users within your organization",
  "Status Dashboard":
    "View system status, announcements, and activity overview",
  "Vendor Discovery": "Search and find potential vendors through the platform",
  "Document Library": "Access and manage documents and templates",
  // "Compare Vendors": "View side-by-side comparisons of vendor capabilities",
  "Trial Experience Dashboard":
    "Access dashboard for comparing multiple vendors and analysis",
  "Receive Platform Notifications":
    "Get system alerts and updates about relevant activities",

  // VQQ Permissions (internal keys unchanged)
  "Diligent Respond Access":
    "Access the Diligent Respond platform to complete and submit Vendor Qualification Questionnaires (VQQs)",
  "Request RFIs": "Create and submit new VQQ requests to vendors",
  "View Requested RFIs":
    "Access and view the Vendor Qualification Questionnaires (VQQs) that have been requested by your organization",
  "Manage Requested & Released RFIs":
    "Edit, update, and control both draft and published VQQs",
  "View Released RFIs":
    "Access finalized VQQs that have been released to vendors",
  "RFI Template Builder":
    "Create and customize templates for future VQQ requests",
  "Risk Categorization":
    "Assign and manage risk levels for different vendors and requests",
  "Download RFIs": "Export and save VQQ documents and responses locally",
  "My Vendors":
    "View released documents and risk scoring from your requested vendor list.",
  "Request New RFIs":
    "Initiate fresh VQQ requests to vendors not previously engaged",
  "Compare RFI":
    "Compare responses across multiple Vendor Qualification Questionnaires (VQQs) side by side",

  // Assessment Permissions
  "Request Assessment": "Initiate new vendor assessment requests",
  "Shared Assessment Calendar":
    "View and manage scheduled assessments in the calendar",
  "View and Request Library VQAs":
    "Access validation and quality assessment reports from the library",
  "Request/Manage New Assessments":
    "Create and oversee new vendor evaluation processes",
  "View My Assessment":
    "Access assessments specifically assigned to you or your team",
  "View Scheduled Assessments":
    "See upcoming and planned assessment activities",
  "Join Scheduled Assessments":
    "Participate in assessment meetings and evaluation sessions",

  // Audit Permissions
  "Request Audit Reports":
    "Initiate requests for new audit reports from vendors",
  "View Audit Reports":
    "Access and read audit documentation provided by vendors",
  "Download Audit Reports": "Save audit reports locally for offline reference",

  // Admin Permissions
  "Manage Team": "Add, remove, and organize team members and their assignments",
  "Manage Organization": "Configure organization-wide settings and hierarchies",
};

const AddSponsorAccount = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const sponsorId = searchParams.get("sponsorId");
  const sponsorName = searchParams.get("sponsorName");
  const id = searchParams.get("id");

  const decodeId = atob(id);

  const [show, setShow] = React.useState(false);
  const [message, setMessage] = React.useState("");
  const { mutate: addSponsor, isError } = useAddSponsorUser();

  // Track expanded roles
  const [expandedRoles, setExpandedRoles] = React.useState({});

  // Track rights for each role
  const [selectedRights, setSelectedRights] = React.useState({});

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      contact_preference: "Mobile_Phone",
      role: [],
    },
  });

  const selectedRoles = watch("role") || [];

  // Update the useEffect hook to properly handle permission changes
  React.useEffect(() => {
    // Only run when roles change
    const applyRolePermissions = () => {
      // Step 1: Clear all existing permissions first
      rightsList.forEach((right) => {
        setValue(right, false, { shouldDirty: true });
      });

      // Step 2: If we have any roles selected, apply appropriate permissions
      if (selectedRoles.length > 0) {
        // Always set general permissions first (except Trial Experience Dashboard which requires explicit selection)
        rightsCategories["General Permissions"].rights.forEach((right) => {
          if (right !== "Trial Experience Dashboard") {
            setValue(right, true, { shouldDirty: true });
          }
        });

        // Then apply role-specific permissions
        selectedRoles.forEach((role) => {
          if (role === "Sponsor Admin") {
            // Admin gets all permissions (except Trial Experience Dashboard which requires explicit selection)
            rightsList.forEach((right) => {
              if (right !== "Trial Experience Dashboard") {
                setValue(right, true, { shouldDirty: true });
              }
            });
          } else if (role === "RFI Manager") {
            // Set RFI permissions
            rightsCategories["RFI Permissions"].rights.forEach((right) => {
              setValue(right, true, { shouldDirty: true });
            });
          } else if (role === "VQA Manager") {
            // Set Assessment permissions
            rightsCategories["Assessment Permissions"].rights.forEach(
              (right) => {
                setValue(right, true, { shouldDirty: true });
              }
            );
          }
        });
      }
    };

    // Apply permissions whenever roles change
    applyRolePermissions();

    // We don't need to watch here, as setValue doesn't impact the dependency
  }, [selectedRoles, setValue]); // Remove watch from dependencies

  // Handle role selection with immediate UI update
  const handleRoleChange = (e, role) => {
    const isChecked = e.target.checked;

    // Get current roles
    const currentRoles = getValues("role") || [];
    let newRoles = [];

    if (isChecked) {
      // If selecting Sponsor Admin, only allow it
      if (role === "Sponsor Admin") {
        newRoles = ["Sponsor Admin"];
      }
      // If selecting another role when Sponsor Admin is selected, switch to just this role
      else if (currentRoles.includes("Sponsor Admin")) {
        newRoles = [role];
      }
      // Otherwise add this role to existing selections
      else {
        newRoles = [...currentRoles, role];
      }
    } else {
      // If unchecking a role, just remove it
      newRoles = currentRoles.filter((r) => r !== role);
    }

    // Update the form with new role selections
    setValue("role", newRoles);

    // Don't handle permissions here - let the useEffect do it
  };

  // Function to check if a role should be disabled
  const isRoleDisabled = (role) => {
    const currentRoles = getValues("role") || [];
    if (role === "Sponsor Admin") {
      // Disable Sponsor Admin if any other role is selected
      return currentRoles.some((r) => r !== "Sponsor Admin" && r !== role);
    } else {
      // Disable other roles if Sponsor Admin is selected
      return currentRoles.includes("Sponsor Admin");
    }
  };

  const toggleRoleExpansion = (role) => {
    setExpandedRoles((prev) => ({
      ...prev,
      [role]: !prev[role],
    }));
  };

  const onSubmit = (data) => {
    // Ensure we have selected roles
    if (!selectedRoles || selectedRoles.length === 0) {
      setMessage("Please select at least one role");
      setShow(true);
      return;
    }

    // Get only the rights that are checked
    const rights = rightsList.filter((right) => data[right] === true);

    // Prepare roles as array
    const rolesArray = Array.isArray(selectedRoles)
      ? selectedRoles
      : [selectedRoles];

    const sponsorData = {
      email: data.email,
      phone_number: data.phone_number,
      office_phone_number: data.office_phone_number,
      contact_preference: data.contact_preference,
      first_name: data.first_name,
      last_name: data.last_name,
      sponsor_id: sponsorId,
      sponsor_name: sponsorName,
      hypercomply_org_id: Number(decodeId),

      // Generate sponsor_subrole_dto with unique IDs
      sponsor_subrole_dto: rolesArray.map((role) => ({
        sponsor_subrole_id: uuidv4(),
        sub_role: role,
      })),

      // Generate sponsor_rights_dto with unique IDs
      sponsor_rights_dto: rights.map((right) => ({
        sponsor_rights_id: uuidv4(),
        rights: right,
      })),
    };

    addSponsor(sponsorData, {
      onSuccess: () => {
        setMessage("Sponsor added successfully");
        setShow(true);
        setTimeout(
          () => navigate(`/Backend/Users/Sponsors/list/${sponsorId}`),
          2000
        );
      },
      onError: (error) => {
        setMessage(error.response?.data?.message || "Failed to add sponsor");
        setShow(true);
      },
    });
  };

  return (
    <div className="w-full px-5">
      <div className="mt-6 pb-5 space-y-3 border-b border-gray-200 px-0 md:px-1">
        <h1 className="font-semibold">Add New Sponsor</h1>
        <p className="text-sm text-gray-600">
          Fill in the information below to create a new Sponsor account.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="w-full">
        {/* Email */}
        <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6 mt-4 border-b border-gray-300 py-5 lg:py-6">
          <label className="block text-sm font-medium text-gray-900 sm:pt-1.5">
            Email
          </label>
          <div className="mt-2 sm:col-span-2 sm:mt-0">
            <input
              {...register("email")}
              type="email"
              className="ring-0 border-gray-300 rounded-md text-sm w-[300px] lg:w-[400px] xl:w-[500px]"
            />
            {errors.email && (
              <p className="mt-2 text-sm text-red-600">
                {errors.email.message}
              </p>
            )}
          </div>
        </div>

        {/* Roles section */}
        <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6 mt-4 border-b border-gray-300 py-5 lg:py-6">
          <label className="block text-sm font-medium text-gray-900 sm:pt-1.5">
            Roles
          </label>
          <div className="mt-2 sm:col-span-2 sm:mt-0">
            <fieldset className="rounded-md bg-white w-[300px] lg:w-[400px] xl:w-[500px]">
              <div className="">
                {Object.keys(roleDefinitions).map((role) => (
                  <div key={role} className="p-4">
                    <div
                      className={`flex items-center ${
                        isRoleDisabled(role) ? "opacity-50" : ""
                      }`}
                    >
                      <input
                        type="checkbox"
                        id={`role-${role}`}
                        value={role}
                        checked={selectedRoles.includes(role)}
                        onChange={(e) => handleRoleChange(e, role)}
                        disabled={isRoleDisabled(role)}
                        className={`h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 ${
                          isRoleDisabled(role)
                            ? "cursor-normal"
                            : "cursor-pointer"
                        }`}
                      />
                      <label htmlFor={`role-${role}`} className="ml-3 flex-1">
                        <span className="block text-sm font-medium text-gray-900">
                          {ROLE_DISPLAY_NAMES[role] || role}
                        </span>
                        <span className="block text-sm text-gray-500 mt-1">
                          {roleDefinitions[role].description}
                        </span>
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </fieldset>
            {errors.role && (
              <p className="mt-2 text-sm text-red-600">{errors.role.message}</p>
            )}

            {/* Hidden inputs to ensure role is registered with React Hook Form */}
            <input type="hidden" {...register("role")} />
          </div>
        </div>

        {/* Rights Groups Section */}
        {
          <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6 mt-4 py-5 lg:py-6">
            <label className="block text-sm font-medium text-gray-900 sm:pt-1.5">
              Permissions
            </label>
            <div className="mt-2 sm:col-span-2 sm:mt-0">
              <div className="space-y-6 w-[300px] lg:w-[400px] xl:w-[500px]">
                {Object.entries(rightsCategories).map(
                  ([category, categoryData]) => (
                    <div key={category} className="rounded-md">
                      <div className="px-4 py-3">
                        <h3 className="text-sm font-medium text-gray-900">
                          {PERMISSION_CATEGORY_DISPLAY_NAMES[category] ||
                            category}
                        </h3>
                      </div>
                      <div className="px-4 py-3">
                        <div className="space-y-4">
                          {categoryData.rights.map((rightItem) => {
                            // Get the current value from the form
                            const currentValue = watch(rightItem) || false;

                            // Determine if this right belongs to any of the selected roles
                            const isRoleSpecificRight = selectedRoles.some(
                              (role) => {
                                if (role === "Sponsor Admin") return true; // Admin can modify all rights

                                // For other roles, check if this right belongs to their category
                                if (
                                  role === "RFI Manager" &&
                                  category === "RFI Permissions"
                                )
                                  return true;
                                if (
                                  role === "VQA Manager" &&
                                  category === "Assessment Permissions"
                                )
                                  return true;

                                return false;
                              }
                            );

                            // General permissions are always editable if any role is selected
                            const isGeneralPermission =
                              category === "General Permissions" &&
                              selectedRoles.length > 0;

                            // Admin permissions are only editable by Sponsor Admin
                            const isAdminPermission =
                              category === "Admin Permissions" &&
                              selectedRoles.includes("Sponsor Admin");

                            // Determine if this checkbox should be editable
                            const isEditable =
                              isRoleSpecificRight ||
                              isGeneralPermission ||
                              isAdminPermission;

                            return (
                              <div key={rightItem} className="flex items-start">
                                <div className="flex items-center h-5 pt-1">
                                  <input
                                    type="checkbox"
                                    id={`right-${rightItem}`}
                                    name={rightItem} // Add explicit name to ensure unique form fields
                                    checked={currentValue}
                                    disabled={!isEditable}
                                    onChange={(e) => {
                                      // Important: Only update this specific right's value
                                      setValue(rightItem, e.target.checked, {
                                        shouldDirty: true,
                                        shouldValidate: true,
                                      });
                                    }}
                                    className={`h-4 w-4 rounded border-gray-300 ${
                                      isEditable
                                        ? "text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                                        : "text-gray-300 cursor-not-allowed"
                                    }`}
                                  />
                                </div>
                                <div
                                  className={`ml-3 text-sm ${
                                    !isEditable ? "opacity-50" : ""
                                  }`}
                                >
                                  <label
                                    htmlFor={`right-${rightItem}`}
                                    className={`font-medium ${
                                      isEditable
                                        ? "text-gray-700"
                                        : "text-gray-400"
                                    }`}
                                  >
                                    {RIGHTS_DISPLAY_NAMES[rightItem] || rightItem}
                                  </label>
                                  <p className="text-xs text-gray-500 mt-1">
                                    {rightsDescriptions[rightItem] ||
                                      "No description available"}
                                  </p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )
                )}
              </div>

              {/* Hidden inputs for all rights */}
              {rightsList.map((right) => (
                <input
                  type="hidden"
                  key={`hidden-${right}`}
                  {...register(right)}
                />
              ))}
            </div>
          </div>
        }

        {/* First Name */}
        <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6 mt-4 border-b border-gray-300 py-5 lg:py-6">
          <label className="block text-sm font-medium text-gray-900 sm:pt-1.5">
            First Name
          </label>
          <div className="mt-2 sm:col-span-2 sm:mt-0">
            <input
              {...register("first_name")}
              type="text"
              className="ring-0 border-gray-300 rounded-md text-sm w-[300px] lg:w-[400px] xl:w-[500px]"
            />
            {errors.first_name && (
              <p className="mt-2 text-sm text-red-600">
                {errors.first_name.message}
              </p>
            )}
          </div>
        </div>

        {/* Last Name */}
        <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6 mt-4 border-b border-gray-300 py-5 lg:py-6">
          <label className="block text-sm font-medium text-gray-900 sm:pt-1.5">
            Last Name
          </label>
          <div className="mt-2 sm:col-span-2 sm:mt-0">
            <input
              {...register("last_name")}
              type="text"
              className="ring-0 border-gray-300 rounded-md text-sm w-[300px] lg:w-[400px] xl:w-[500px]"
            />
            {errors.last_name && (
              <p className="mt-2 text-sm text-red-600">
                {errors.last_name.message}
              </p>
            )}
          </div>
        </div>

        {/* Office Phone Number */}
        <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6 mt-4 border-b border-gray-300 py-5 lg:py-6">
          <label className="block text-sm font-medium text-gray-900 sm:pt-1.5">
            Office Phone Number
          </label>
          <div className="mt-2 sm:col-span-2 sm:mt-0">
            <input
              {...register("office_phone_number")}
              type="tel"
              className="ring-0 border-gray-300 rounded-md text-sm w-[300px] lg:w-[400px] xl:w-[500px]"
            />
            {errors.office_phone_number && (
              <p className="mt-2 text-sm text-red-600">
                {errors.office_phone_number.message}
              </p>
            )}
          </div>
        </div>

        {/* Phone Number */}
        <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6 mt-4 border-b border-gray-300 py-5 lg:py-6">
          <label className="block text-sm font-medium text-gray-900 sm:pt-1.5">
            Phone Number
          </label>
          <div className="mt-2 sm:col-span-2 sm:mt-0">
            <input
              {...register("phone_number")}
              type="tel"
              className="ring-0 border-gray-300 rounded-md text-sm w-[300px] lg:w-[400px] xl:w-[500px]"
            />
          </div>
        </div>

        {/* Contact Preference */}
        <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6 mt-4 border-b border-gray-300 py-5 lg:py-6">
          <label className="block text-sm font-medium text-gray-900 sm:pt-1.5">
            Contact Preference
          </label>
          <div className="mt-2 sm:col-span-2 sm:mt-0">
            <select
              {...register("contact_preference")}
              className="ring-0 border-gray-300 rounded-md text-sm w-[300px] lg:w-[400px] xl:w-[500px] [&>option]:bg-white [&>option:hover]:bg-indigo-600 [&>option:hover]:text-white"
            >
              <option value="Mobile_Phone">Mobile Phone</option>
              <option value="Office_Phone">Office Phone</option>
            </select>
          </div>
        </div>

        {/* Submit Button */}
        <div className="mt-10 flex items-center justify-end gap-x-6 py-2 px-5">
          <button
            type="submit"
            className="inline-flex justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm bg-indigo-600 hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          >
            Submit
          </button>
        </div>
      </form>

      <NotificationModal
        open={show}
        setOpen={setShow}
        desc={message}
        title={isError ? "Error" : "Success"}
        error={isError}
      />
    </div>
  );
};

export default AddSponsorAccount;
